import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ProviderReview } from '@/types';

// Transform database row (snake_case) to TypeScript (camelCase)
function transformReview(row: Record<string, unknown>): ProviderReview {
  return {
    id: row.id as string,
    providerId: row.provider_id as string,
    userId: row.user_id as string,
    userName: row.user_name as string | null,
    rating: row.rating as number,
    title: row.title as string | null,
    content: row.content as string | null,
    serviceType: row.service_type as ProviderReview['serviceType'],
    verifiedCustomer: row.verified_customer as boolean,
    response: row.response as string | null,
    respondedAt: row.responded_at ? new Date(row.responded_at as string) : null,
    createdAt: new Date(row.created_at as string),
  };
}

async function fetchReviews(providerId: string): Promise<ProviderReview[]> {
  const { data, error } = await supabase
    .from('provider_reviews')
    .select('*')
    .eq('provider_id', providerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }

  return (data || []).map(transformReview);
}

export function useProviderReviews(providerId?: string) {
  return useQuery({
    queryKey: ['provider-reviews', providerId],
    queryFn: () => fetchReviews(providerId!),
    enabled: !!providerId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
